import React from 'react';
import PropTypes from 'prop-types';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';

const FIELDS_BY_TYPE = {
  Edital: [
    ['edital_number', 'Número'],
    ['edital_year', 'Ano'],
    ['edital_type', 'Tipo'],
    ['edital_status', 'Status'],
    ['opening_date', 'Abertura'],
    ['deadline', 'Prazo'],
  ],
  Audiencia: [
    ['audience_type', 'Tipo'],
    ['audience_date', 'Data'],
    ['recording_url', 'Gravação'],
  ],
  Noticia: [
    ['categoria', 'Categoria'],
    ['source_url', 'Fonte'],
  ],
};

const LONG_FIELDS = {
  Edital: [],
  Audiencia: ['agenda'],
  Noticia: [],
};

const PTYPE_LABELS = {
  Noticia: 'Notícia',
  Edital: 'Edital',
  Audiencia: 'Audiência',
};

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') return null;
  // Choice field do Plone REST: { token, title }
  if (typeof value === 'object' && value !== null && 'title' in value) {
    return value.title;
  }
  if (key.endsWith('_value')) {
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    } catch { return String(value); }
  }
  if (key.endsWith('_date') || key === 'deadline' || key === 'opening_date' || key === 'audience_date') {
    try {
      const d = new Date(value);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return String(value); }
  }
  if (key.endsWith('_url')) {
    return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
  }
  return String(value);
};

const EntityView = ({ content }) => {
  const ptype = content?.['@type'];
  const fields = FIELDS_BY_TYPE[ptype] || [];
  const longFields = LONG_FIELDS[ptype] || [];

  const imageUrl =
    content?.image?.scales?.large?.download ||
    content?.image?.scales?.preview?.download ||
    content?.image?.download;

  const blocksData = content?.blocks || {};
  const layout = (content?.blocks_layout?.items || []).filter((id) => {
    const t = blocksData[id]?.['@type'];
    return t !== 'title' && t !== 'description';
  });
  const filteredContent = {
    ...content,
    blocks: blocksData,
    blocks_layout: { items: layout },
  };

  return (
    <article className="ag-entity-view">
      <div className="ag-entity-view__inner">
        <div className="ag-entity-view__head">
          {ptype && (
            <span className="ag-entity-view__eyebrow">{PTYPE_LABELS[ptype] || ptype}</span>
          )}
          <h1 className="ag-entity-view__title">{content.title}</h1>
          {content.description && (
            <p className="ag-entity-view__description">{content.description}</p>
          )}
        </div>

        {fields.length > 0 && (
          <dl className="ag-entity-view__meta">
            {fields.map(([key, label]) => {
              const v = formatValue(key, content[key]);
              if (v === null) return null;
              return (
                <div key={key} className="ag-entity-view__meta-row">
                  <dt>{label}</dt>
                  <dd>{v}</dd>
                </div>
              );
            })}
          </dl>
        )}

        {longFields.map((key) => {
          const v = content[key];
          if (!v) return null;
          return (
            <section key={key} className="ag-entity-view__long">
              <h2>{key === 'contract_object' ? 'Objeto' : key === 'agenda' ? 'Pauta' : key}</h2>
              <p>{v}</p>
            </section>
          );
        })}

        {imageUrl && (
          <div className="ag-entity-view__image">
            <img src={imageUrl} alt={content.title} />
          </div>
        )}

        {layout.length > 0 && (
          <div className="ag-entity-view__body">
            <RenderBlocks content={filteredContent} />
          </div>
        )}
      </div>
    </article>
  );
};

EntityView.propTypes = {
  content: PropTypes.object.isRequired,
};

export default EntityView;
